import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PlanUsageCard } from "./PlanUsageCard";

import { Link as RouterLink } from "react-router-dom";

import {
  useArchiveSchool,
  useDeleteSchool,
  useSaveSchool,
  useSchools,
  useOrgMe,
  useUnarchiveSchool,
  type SchoolFull,
} from "@/api/school";

/** 学校管理 — group-admin CRUD over school_schools (org P1: 集团-学校 layer).
 *  Custom name + logo per Jarod's spec; delete blocked server-side while a
 *  school still has classes/students. */
export default function SchoolsAdminPage() {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const schools = useSchools(showArchived);
  const del = useDeleteSchool();
  const unarchive = useUnarchiveSchool();
  // 园长只读:建校/改名/删校/归档均为集团管理员专属(后端 require_admin),
  // 不隐藏的话园长点了必 403(Jarod 2026-08-27 学校管理页反馈)。
  const isGroupAdmin = useOrgMe().data?.is_admin === true;
  const [editing, setEditing] = useState<SchoolFull | "new" | null>(null);
  // §5.4: 删除被拦截后就地展开处置面板,不再是一行死路文案
  const [intercept, setIntercept] = useState<{
    school: SchoolFull;
    counts: { classes: number; students: number; teachers: number };
  } | null>(null);
  const [archiving, setArchiving] = useState<SchoolFull | null>(null);

  async function tryDelete(s: SchoolFull) {
    if (!window.confirm(t("orgSchools.deleteConfirm", { name: s.name }))) return;
    try {
      await del.mutateAsync(s.id);
    } catch (e) {
      const d = (e as { detail?: { error?: string; counts?: { classes: number; students: number; teachers: number }; message?: string } }).detail;
      if (d?.error === "SCHOOL_NOT_EMPTY" && d.counts) setIntercept({ school: s, counts: d.counts });
      else if (d?.message) window.alert(d.message);
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{t("orgSchools.title")}</h1>
          <p className="text-xs text-slate-500 mt-1">{t("orgSchools.hint")}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            {t("orgSchools.showArchived")}
          </label>
          {isGroupAdmin && (
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
          >
            {t("orgSchools.add")}
          </button>
        )}
        </div>
      </header>

      {schools.isLoading && <p className="text-sm text-slate-400">…</p>}
      {schools.error != null && (
        <p className="text-sm text-rose-500">{String(schools.error)}</p>
      )}

      {/* 租户级 AI 配额 —— 全集团一个池子,只在集团层展示一次。 */}
      <PlanUsageCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(schools.data ?? []).map((s) => (
          <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <Link to={`/admin/schools/${s.id}`} className="flex items-center gap-3 group">
              {s.logo ? (
                <img src={s.logo} alt="" className="h-10 w-10 rounded object-contain" />
              ) : (
                <span className="text-2xl">🏫</span>
              )}
              <div className="min-w-0">
                <p className="font-medium text-slate-800 truncate group-hover:text-sky-700">
                  {s.name}
                  {s.status === "archived" && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 text-[10px] align-middle">
                      {t("orgSchools.archivedBadge")}
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-400">{s.code}</p>
              </div>
              <span className="ml-auto text-slate-300 group-hover:text-sky-500">→</span>
            </Link>
            <div className={`mt-3 flex gap-2 ${isGroupAdmin ? "" : "hidden"}`}>
              <button
                type="button"
                onClick={() => setEditing(s)}
                className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                {t("orgSchools.edit")}
              </button>
              {s.status === "archived" ? (
                <button
                  type="button"
                  onClick={() => unarchive.mutate(s.id)}
                  className="px-2 py-1 text-xs rounded border border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                >
                  {t("orgSchools.unarchive")}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={del.isPending}
                  onClick={() => void tryDelete(s)}
                  className="px-2 py-1 text-xs rounded border border-rose-200 text-rose-500 hover:bg-rose-50"
                >
                  {t("orgSchools.delete")}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <SchoolFormModal school={editing === "new" ? null : editing} onClose={() => setEditing(null)} />
      )}

      {intercept && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={() => setIntercept(null)}>
          <div className="w-[26rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-slate-800 mb-2">
              ⚠️ {t("orgSchools.intercept.title", { name: intercept.school.name })}
            </h2>
            <ul className="mb-3 space-y-1.5 text-sm text-slate-700">
              <li className="flex justify-between items-center">
                <span>{t("orgSchools.intercept.classes", { n: intercept.counts.classes })}</span>
                <RouterLink to={`/admin/schools/${intercept.school.id}?tab=classes`} className="text-xs text-sky-600 hover:underline">
                  {t("orgSchools.intercept.viewClasses")}
                </RouterLink>
              </li>
              <li className="flex justify-between items-center">
                <span>{t("orgSchools.intercept.students", { n: intercept.counts.students })}</span>
                <RouterLink to={`/admin/schools/${intercept.school.id}?tab=students`} className="text-xs text-sky-600 hover:underline">
                  {t("orgSchools.intercept.transferStudents")}
                </RouterLink>
              </li>
              <li className="flex justify-between items-center">
                <span>{t("orgSchools.intercept.teachers", { n: intercept.counts.teachers })}</span>
                <RouterLink to={`/admin/schools/${intercept.school.id}?tab=teachers`} className="text-xs text-sky-600 hover:underline">
                  {t("orgSchools.intercept.transferTeachers")}
                </RouterLink>
              </li>
            </ul>
            <p className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">
              {t("orgSchools.intercept.hint")}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setIntercept(null)} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
                {t("common.cancel")}
              </button>
              <button
                onClick={() => { setArchiving(intercept.school); setIntercept(null); }}
                className="px-3 py-1.5 rounded bg-amber-500 text-white text-sm hover:bg-amber-600"
              >
                {t("orgSchools.intercept.archive")}
              </button>
            </div>
          </div>
        </div>
      )}

      {archiving && <ArchiveConfirmModal school={archiving} onClose={() => setArchiving(null)} />}
    </div>
  );
}

function SchoolFormModal({ school, onClose }: { school: SchoolFull | null; onClose: () => void }) {
  const { t } = useTranslation();
  const save = useSaveSchool();
  const [form, setForm] = useState({
    name: school?.name ?? "",
    code: school?.code ?? "",
    logo: school?.logo ?? "",
  });

  function pickLogo(file: File) {
    if (file.size > 400_000) {
      window.alert(t("orgSchools.logoTooBig"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: String(reader.result) }));
    reader.readAsDataURL(file);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          {school ? t("orgSchools.edit") : t("orgSchools.add")}
        </h2>
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="text-slate-600">{t("orgSchools.name")}</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">{t("orgSchools.code")}</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">Logo</span>
            <div className="mt-1 flex items-center gap-2">
              {form.logo && <img src={form.logo} alt="" className="h-9 w-9 object-contain" />}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && pickLogo(e.target.files[0])}
              />
            </div>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={!form.name.trim() || !form.code.trim() || save.isPending}
            onClick={() => save.mutate({ id: school?.id, ...form }, { onSuccess: onClose })}
            className="px-3 py-1.5 text-sm rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 归档确认:输入学校名称全称防误操作 (§5.4)。 */
function ArchiveConfirmModal({ school, onClose }: { school: SchoolFull; onClose: () => void }) {
  const { t } = useTranslation();
  const archive = useArchiveSchool();
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    try {
      await archive.mutateAsync({ id: school.id, confirm_name: name });
      onClose();
    } catch (e) {
      const d = (e as { detail?: { message?: string } }).detail;
      setErr(d?.message ?? (e as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[24rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-slate-800 mb-2">{t("orgSchools.archiveTitle", { name: school.name })}</h2>
        <p className="mb-3 text-xs text-slate-500">{t("orgSchools.archiveHint")}</p>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={school.name}
          className="mb-2 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
        {err && <p className="mb-2 text-xs text-rose-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
            {t("common.cancel")}
          </button>
          <button
            disabled={name.trim() !== school.name || archive.isPending}
            onClick={submit}
            className="px-3 py-1.5 rounded bg-amber-500 text-white text-sm disabled:opacity-50"
          >
            {t("orgSchools.archiveConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
